import React, { useCallback, useContext } from "react";
import { Color1Set, Shikii1Set } from "../App";
import JSZip from "jszip";
import { saveAs } from "file-saver";

function buildMetaOverride(meta, color1set, shikii1set) {
  const c = Array.isArray(color1set) ? color1set : [];
  const s = Array.isArray(shikii1set) ? shikii1set : [];

  const pickColor = (i, fallback) => {
    const v = c[i];
    return v != null && v !== "" ? v : fallback;
  };

  return {
    ...meta,

    kml1_color: pickColor(0, meta.kml1_color),
    kml2_color: pickColor(1, meta.kml2_color),
    kml3_color: pickColor(2, meta.kml3_color),
    kml4_color: pickColor(3, meta.kml4_color),
    kml5_color: pickColor(4, meta.kml5_color),

    hanrei1_color: pickColor(0, meta.hanrei1_color),
    hanrei2_color: pickColor(1, meta.hanrei2_color),
    hanrei3_color: pickColor(2, meta.hanrei3_color),
    hanrei4_color: pickColor(3, meta.hanrei4_color),
    hanrei5_color: pickColor(4, meta.hanrei5_color),

    shikii1: s[0] ?? meta.shikii1,
    shikii2: s[1] ?? meta.shikii2,
    shikii3: s[2] ?? meta.shikii3,
    shikii4: s[3] ?? meta.shikii4,
    shikii5: s[4] ?? meta.shikii5,
  };
}

/**
 * =========================
 * Legend drawing helpers
 * =========================
 */

function buildLegendState(metadata) {
  const hasCategory = !!metadata.category_data;

  const s1 = Number(metadata.shikii1);
  const s2 = Number(metadata.shikii2);
  const s3 = Number(metadata.shikii3);
  const s4 = Number(metadata.shikii4);
  const s5 = Number(metadata.shikii5);

  const eqNum = (a, b) => Number.isFinite(a) && Number.isFinite(b) && a === b;
  const fmt = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? "" : String(v));

  const hide4 = !hasCategory && eqNum(s4, s3);
  const hide5 = !hasCategory && (eqNum(s5, s4) || eqNum(s4, s3));

  const rangeLabels = [
    `${fmt(s1)}〜`,
    `${fmt(s2)}〜${fmt(s1)}`,
    `${fmt(s3)}〜${fmt(s2)}`,
    `${fmt(s4)}〜${fmt(s3)}`,
    `〜${fmt(s4)}`,
  ];

  const legendText = (idx) => {
    if (hasCategory) return metadata[`hanrei${idx}_name`] ?? "";
    return rangeLabels[idx - 1] ?? "";
  };

  return {
    hide4,
    hide5,
    legendText,
  };
}

function drawClosedPath(ctx, points) {
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i][0], points[i][1]);
  }
  ctx.closePath();
}

function fillPolygon(ctx, points, fillStyle) {
  drawClosedPath(ctx, points);
  ctx.fillStyle = fillStyle;
  ctx.fill();
}

function fillAndStrokePolygon(ctx, points, fillStyle) {
  drawClosedPath(ctx, points);
  ctx.fillStyle = fillStyle;
  ctx.fill();
  ctx.stroke();
}

function strokeLine(ctx, x1, y1, x2, y2) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

function fillLegendBox(ctx, x1, y1, x2, y2, color) {
  fillPolygon(
    ctx,
    [
      [x2, y2],
      [x1, y2],
      [x1, y1],
      [x2, y1],
    ],
    color
  );
}

function drawLegendBase(ctx, alpha) {
  ctx.globalAlpha = alpha * 0.75;
  fillPolygon(
    ctx,
    [
      [160.6, 99.2],
      [0.0, 99.2],
      [0.0, 0.0],
      [160.6, 0.0],
      [160.6, 99.2],
    ],
    "rgb(254, 254, 254)"
  );
}

function drawYearPanel(ctx, alpha, year) {
  ctx.globalAlpha = alpha * 0.75;
  ctx.lineWidth = 0.3;
  fillAndStrokePolygon(
    ctx,
    [
      [156.0, 97.4],
      [123.8, 97.4],
      [123.8, 85.9],
      [156.0, 85.9],
      [156.0, 97.4],
    ],
    "rgb(254, 254, 254)"
  );

  ctx.globalAlpha = alpha * 1.0;
  ctx.font = "9.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  ctx.fillStyle = "rgb(0, 0, 0)";
  ctx.fillText(year, 128.0, 95.1);
}

function drawLegendCommon(ctx, metadata, rr_ratio) {
  const alpha = ctx.globalAlpha;
  const { hide4, hide5, legendText } = buildLegendState(metadata);

  ctx.save();

  drawLegendBase(ctx, alpha);
  ctx.globalAlpha = alpha * 1.0;

  fillPolygon(
    ctx,
    [
      [105.0, 48.0],
      [92.3, 49.2],
      [82.7, 92.5],
      [84.8, 94.0],
      [90.3, 94.2],
      [115.5, 52.9],
      [105.0, 48.0],
    ],
    metadata.hanrei1_color
  );

  fillPolygon(
    ctx,
    [
      [89.8, 65.7],
      [85.0, 61.7],
      [74.5, 62.0],
      [74.3, 94.0],
      [77.7, 95.9],
      [82.8, 94.9],
      [89.8, 65.7],
    ],
    metadata.hanrei2_color
  );

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(89.5, 65.9);
  ctx.lineTo(79.3, 66.7);
  ctx.lineTo(74.6, 62.3);
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = "rgb(254, 254, 254)";
  ctx.lineCap = "round";
  ctx.stroke();
  strokeLine(ctx, 77.5, 95.7, 79.3, 66.7);
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(115.3, 53.2);
  ctx.lineTo(101.7, 53.4);
  ctx.lineTo(92.5, 49.5);
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = "rgb(254, 254, 254)";
  ctx.lineCap = "round";
  ctx.stroke();
  strokeLine(ctx, 85.2, 94.2, 101.7, 53.4);
  ctx.restore();

  fillPolygon(
    ctx,
    [
      [75.2, 80.4],
      [71.2, 75.0],
      [62.2, 76.7],
      [64.7, 93.2],
      [68.0, 96.0],
      [73.7, 94.4],
      [75.2, 80.4],
    ],
    metadata.hanrei3_color
  );

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(62.5, 76.9);
  ctx.lineTo(67.3, 81.9);
  ctx.lineTo(74.8, 80.5);
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = "rgb(254, 254, 254)";
  ctx.lineCap = "round";
  ctx.stroke();
  strokeLine(ctx, 68.0, 96.0, 67.3, 81.9);
  ctx.restore();

  ctx.beginPath();
  ctx.moveTo(117.3, 53.2);
  ctx.lineTo(122.4, 53.2);
  ctx.lineWidth = 0.5;
  ctx.lineCap = "round";
  ctx.stroke();

  strokeLine(ctx, 120.1, 53.2, 120.1, 94.4);

  fillPolygon(
    ctx,
    [
      [122.3, 57.5],
      [117.7, 57.4],
      [120.1, 52.9],
      [122.3, 57.5],
    ],
    "rgb(0, 0, 0)"
  );

  fillPolygon(
    ctx,
    [
      [117.8, 89.9],
      [122.4, 90.0],
      [120.1, 94.5],
      [117.8, 89.9],
    ],
    "rgb(0, 0, 0)"
  );

  ctx.font = "5.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  const ratioLabel =
    rr_ratio != null && rr_ratio !== ""
      ? `  (${rr_ratio}倍)`
      : "";

  ctx.fillText(
    (metadata.height_ratio || "") + ratioLabel,
    110,
    50
  );

  ctx.font = "5.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  ctx.fillText("高さ：", 122.8, 62);
  ctx.fillText(metadata.hanrei_height_name, 122.8, 70.0);
  ctx.fillText((metadata.hanrei_height_name_2 || ""), 122.8, 77.0);
  ctx.fillText((metadata.hanrei_height_name_3 || ""), 122.8, 84.0);

  ctx.fillText("色：" + metadata.hanrei_color_name, 5.8, 54.3);

  ctx.font = "bold 7.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  ctx.fillText(
    metadata.title_height_name +
      (metadata.title_color_name ? "と" : "") +
      (metadata.title_color_name || ""),
    5,
    45.0
  );

  strokeLine(ctx, 5.0, 56.7, 90.8, 56.7);

  ctx.beginPath();
  ctx.moveTo(91.9, 56.9);
  ctx.bezierCurveTo(91.9, 57.5, 91.4, 58.0, 90.8, 58.0);
  ctx.bezierCurveTo(90.2, 58.0, 89.7, 57.5, 89.7, 56.9);
  ctx.bezierCurveTo(89.7, 56.3, 90.2, 55.8, 90.8, 55.8);
  ctx.bezierCurveTo(91.4, 55.8, 91.9, 56.3, 91.9, 56.9);
  ctx.closePath();
  ctx.fill();

  fillLegendBox(ctx, 5.7, 61.0, 11.2, 66.4, metadata.hanrei1_color);
  ctx.font = "5.0px 'KozGoPro-Regular-90ms-RKSJ-H'";
  ctx.fillStyle = "rgb(0, 0, 0)";
  ctx.fillText(legendText(1), 13.5, 65.2);

  fillLegendBox(ctx, 5.7, 68.5, 11.2, 73.9, metadata.hanrei2_color);
  ctx.fillStyle = "rgb(0, 0, 0)";
  ctx.fillText(legendText(2), 13.5, 72.7);

  fillLegendBox(ctx, 5.7, 76.0, 11.2, 81.4, metadata.hanrei3_color);
  ctx.fillStyle = "rgb(0, 0, 0)";
  ctx.fillText(legendText(3), 13.5, 80.2);

  if (!hide4 && metadata.hanrei4_color) {
    fillLegendBox(ctx, 5.9, 83.5, 11.4, 88.9, metadata.hanrei4_color);
    ctx.fillStyle = "rgb(0, 0, 0)";
    ctx.fillText(legendText(4), 13.7, 87.7);

    fillPolygon(
      ctx,
      [
        [64.7, 85.6],
        [60.7, 82.9],
        [54.3, 86.0],
        [59.2, 96.3],
        [62.3, 97.5],
        [66.2, 95.2],
        [64.7, 85.6],
      ],
      metadata.hanrei4_color
    );

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(54.7, 86.1);
    ctx.lineTo(59.2, 88.3);
    ctx.lineTo(64.5, 85.9);
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = "rgb(254, 254, 254)";
    ctx.lineCap = "round";
    ctx.stroke();
    strokeLine(ctx, 62.3, 97.5, 59.2, 88.3);
    ctx.restore();
  }

  if (!hide5 && metadata.hanrei5_color) {
    fillLegendBox(ctx, 5.9, 91.5, 11.4, 96.4, metadata.hanrei5_color);
    ctx.fillStyle = "rgb(0, 0, 0)";
    ctx.fillText(legendText(5), 13.7, 95.2);

    fillPolygon(
      ctx,
      [
        [56.7, 90.6],
        [53.7, 87.9],
        [48.3, 91.0],
        [51.2, 96.3],
        [54.3, 97.5],
        [58.2, 95.2],
        [56.7, 90.6],
      ],
      metadata.hanrei5_color
    );
  }

  ctx.restore();
}

function drawLegendYear(ctx, year) {
  const alpha = ctx.globalAlpha;
  ctx.save();
  drawYearPanel(ctx, alpha, year);
  ctx.restore();
}

/**
 * =========================
 * Utils
 * =========================
 */

const normalizeNFD = (s) => String(s ?? "").normalize("NFD");

const fetchTextOrThrow = async (url) => {
  const res = await fetch(url, {});
  if (!res.ok) throw new Error(`fetch failed: ${res.status} ${url}`);
  return await res.text();
};

const stripTrailingNewlines = (text) => {
  return String(text ?? "").replace(/\r?\n$/, "").replace(/\r?\n$/, "");
};

const parseCsvRows = (text) => {
  return stripTrailingNewlines(text)
    .split("\n")
    .map((line) => line.replace("\r", ""))
    .map((line) => line.split(","));
};

const fetchCsvRows = async ({ no, year, area, csvFilename }) => {
  const a = normalizeNFD(area);
  const c = normalizeNFD(csvFilename);
  const filename = `/csv/${no}/${a}_${c}_${year}.csv`;
  const text = await fetchTextOrThrow(filename);
  return parseCsvRows(text);
};

const toNumber = (v) => Number(v);

const WIDTH = 330;
const HEIGHT = 240;

function dataUrlToBase64(dataUrl) {
  const idx = dataUrl.indexOf("base64,");
  if (idx < 0) throw new Error("not base64 data url");
  return dataUrl.slice(idx + "base64,".length);
}

function makeLegendCommonPng(meta, rr_ratio, onlyOneYear = false) {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH * 2;
  canvas.height = HEIGHT * 2;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d context not available");

  ctx.setTransform(2, 0, 0, 2, 0, 0);

  if (onlyOneYear) ctx.translate(0, -35);

  drawLegendCommon(ctx, meta, rr_ratio);

  return {
    filename: `${meta.no}_common.png`,
    base64: dataUrlToBase64(canvas.toDataURL("image/png")),
  };
}

function makeLegendYearPngsForYears(meta, years, onlyOneYear = false) {
  const result = [];

  for (const year of years) {
    if (!year) continue;

    const canvas = document.createElement("canvas");
    canvas.width = WIDTH * 2;
    canvas.height = HEIGHT * 2;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d context not available");

    ctx.setTransform(2, 0, 0, 2, 0, 0);

    if (onlyOneYear) ctx.translate(0, -35);

    drawLegendYear(ctx, year);

    result.push({
      year,
      filename: `${meta.no}_${year}_year.png`,
      base64: dataUrlToBase64(canvas.toDataURL("image/png")),
    });
  }

  return result;
}

/**
 * =========================
 * Years builder (cache)
 * =========================
 */
const yearsCache = new Map();

const buildYearsKey = (meta) => {
  if (!meta) return "";

  const ys = Object.keys(meta)
    .filter((k) => /^year\d+$/.test(k))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)))
    .map((k) => (meta[k] ? String(meta[k]) : ""))
    .join("|");

  return `${meta?.no ?? ""}__${ys}`;
};

const getYearKeysSorted = (meta) => {
  if (!meta) return [];
  return Object.keys(meta)
    .filter((k) => /^year\d+$/.test(k))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)));
};

const getYearsSorted = (meta) => {
  const keys = getYearKeysSorted(meta);
  return keys
    .map((k) => meta[k])
    .filter((v) => v != null && v !== "");
};

function makeYears(meta) {
  const key = buildYearsKey(meta);
  if (yearsCache.has(key)) return yearsCache.get(key);

  let yearstmp = [];
  let yearsbeginendtmp = [];

  if (String(meta?.no) === "07-01") {
    yearstmp = ["1月", "4月", "7月", "10月", "1月"];
    yearsbeginendtmp = [
      ["2000-01-01", "2000-04-01"],
      ["2000-04-01", "2000-07-01"],
      ["2000-07-01", "2000-10-01"],
      ["2000-10-01", "2000-12-31"],
    ];
  } else {
    yearstmp = getYearsSorted(meta);

    const firstyear = yearstmp[0];
    const bflastyear = yearstmp.slice(-2)[0];
    const lastyear = yearstmp.slice(-1)[0];

    let lastyearend;
    if (firstyear === lastyear) {
      lastyearend = toNumber(lastyear) + 20;
    } else if (firstyear === bflastyear) {
      lastyearend = toNumber(lastyear) + (toNumber(lastyear) - toNumber(firstyear));
    } else {
      lastyearend = toNumber(lastyear) + (toNumber(lastyear) - toNumber(bflastyear));
    }

    for (let i = 0; i < yearstmp.length; i++) {
      const begin = yearstmp[i];
      const end = yearstmp[i + 1] ?? lastyearend;
      yearsbeginendtmp.push([begin, end]);
    }
  }

  const result = [yearstmp, yearsbeginendtmp];
  yearsCache.set(key, result);
  return result;
}

function isTruthyNihonSokuchi(v) {
  return v === 1 || v === "1" || v === true || v === "true";
}

function buildYearFlags(meta) {
  const yearKeys = Object.keys(meta)
    .filter((k) => /^year\d+$/.test(k))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)));

  return yearKeys
    .map((key, idx) => {
      const year = meta[key];
      if (year == null || year === "") return null;

      return {
        year,
        isNihonSokuchi: isTruthyNihonSokuchi(meta[`year${idx + 1}_nihonsokuchi`]),
      };
    })
    .filter(Boolean);
}

const pickColorIndex = (c, shikii1, shikii2, shikii3, shikii4) => {
  const v = Number(c);
  if (!Number.isFinite(v)) return "5";

  const ts = [shikii1, shikii2, shikii3, shikii4]
    .map((x) => Number(x))
    .filter(Number.isFinite)
    .sort((a, b) => b - a);

  for (let i = 0; i < ts.length; i++) {
    if (v >= ts[i]) return String(i + 1);
  }
  return String(ts.length + 1);
};

const buildPlacemarkKml = ({
  meshcode,
  h,
  c,
  shikii1,
  shikii2,
  shikii3,
  shikii4,
  height_name,
  lon,
  lat,
  lon2,
  lat2,
  latc,
  lonc,
  hdis,
}) => {
  const colorkml1 = pickColorIndex(c, shikii1, shikii2, shikii3, shikii4);

  return `
  <Placemark>
  <name>メッシュ番号：${meshcode}</name>
  <description>${height_name}：${h}</description>
  <styleUrl>#PolyCol${colorkml1}</styleUrl>
  <Polygon>
  <altitudeMode>relativeToGround</altitudeMode>
  <extrude>1</extrude>
  <outerBoundaryIs>
  <LinearRing>
  <coordinates>${lon},${lat},${hdis}
  ${lon2},${lat},${hdis}
  ${lon2},${lat2},${hdis}
  ${lon},${lat2},${hdis}
  ${lon},${lat},${hdis}
  </coordinates>
  </LinearRing>
  </outerBoundaryIs>
  </Polygon>
  <LookAt>
  <latitude>${latc}</latitude>
  <longitude>${lonc}</longitude>
  <altitude>0</altitude>
  <range>1000</range>
  <tilt>0</tilt>
  <heading>0</heading>
  <altitudeMode>relativeToGround</altitudeMode>
  </LookAt>
  </Placemark>
  `;
};

/**
 * =========================
 * Mesh → KML
 * =========================
 */

function mesh2nd2kml3(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));

  const lat = p / 1.5 + q / 12;
  const lon = u + 100 + v * 0.125;
  const lon2 = lon + 0.125;
  const lat2 = lat + 0.08333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

function mesh3rd2kml3(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat = p / 1.5 + q / 12 + r / 120;
  const lon = u + 100 + v * 0.125 + w * 0.0125;
  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

function mesh3rd2kml3_nihon(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat_p = p / 1.5 + q / 12 + r / 120;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;

  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

function mesh4th2kml3(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;
  const latc = lat + 0.008333333 / 4;
  const lonc = lon + 0.0125 / 4;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

function mesh4th2kml3_nihon(meshcode, h, c, ratio, shikii1, shikii2, shikii3, shikii4, height_name, ratio1set) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat_p = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;
  const latc = lat + 0.008333333 / 4;
  const lonc = lon + 0.0125 / 4;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

/**
 * =========================
 * getkml2
 * =========================
 */
async function getkml2(no, year, area, csvFilename, meta, ratio, isNihonSokuchi = false) {
  const rows = await fetchCsvRows({ no, year, area, csvFilename });

  if (!rows?.[0]?.[1]) return { kml: "", rows, meshLen: 0 };

  const meshLen = String(rows[0][0] ?? "").length;
  const n = (v) => (v == null || v === "" ? NaN : Number(v));

  const meshSpecByLen = {
    6: {
      min: () => n(meta.kokudo_min),
      ratio: () => meta.kokudo_ratio,
      shikii: () => [
        meta.kokudo_shikii1,
        meta.kokudo_shikii2,
        meta.kokudo_shikii3,
        meta.kokudo_shikii4 ?? meta.kokudo_shikii3,
      ],
      kml: () => mesh2nd2kml3,
      heightName: () =>
        `${meta.hanrei_height_name || ""}${meta.hanrei_height_name_2 || ""}`,
    },
    8: {
      min: () => n(meta.min),
      ratio: () => meta.ratio,
      shikii: () => [meta.shikii1, meta.shikii2, meta.shikii3, meta.shikii4],
      kml: () => (isNihonSokuchi ? mesh3rd2kml3_nihon : mesh3rd2kml3),
      heightName: () =>
        `${meta.hanrei_height_name || ""}${meta.hanrei_height_name_2 || ""}`,
    },
    9: {
      min: () => n(meta.min),
      ratio: () => meta.ratio,
      shikii: () => [meta.shikii1, meta.shikii2, meta.shikii3, meta.shikii4],
      kml: () => (isNihonSokuchi ? mesh4th2kml3_nihon : mesh4th2kml3),
      heightName: () =>
        `${meta.hanrei_height_name || ""}${meta.hanrei_height_name_2 || ""}`,
    },
  };

  const spec = meshSpecByLen[meshLen];
  if (!spec) return { kml: "", rows, meshLen };

  const minValue = spec.min();
  const ratioValue = spec.ratio();
  const [s1, s2, s3, s4] = spec.shikii();
  const heightName = spec.heightName();
  const fn = spec.kml();

  if (!Number.isFinite(minValue)) return { kml: "", rows, meshLen };

  let tmp = "";
  const cnt = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
  let samples = [];

  for (let i = 0; i < rows.length; i++) {
    const mesh = rows[i][0];
    const h = rows[i][1];
    const c = rows[i][2];

    const hv = n(h);
    if (Number.isFinite(hv) && hv > minValue) {
      const idx = pickColorIndex(c, s1, s2, s3, s4);
      cnt[idx]++;

      if (idx === "3" && samples.length < 5) {
        samples.push({
          c,
          v: Number(c),
          s1: Number(s1),
          s2: Number(s2),
          s3: Number(s3),
          s4: Number(s4),
        });
      }
      tmp += fn(mesh, h, c, ratioValue, s1, s2, s3, s4, heightName, ratio);
    }
  }

  console.log("[kml colorIndex counts]", {
    year,
    isNihonSokuchi,
    cnt,
    shikii: [s1, s2, s3, s4],
    samples,
  });

  return { kml: tmp, rows, meshLen };
}

/**
 * =========================
 * KML folders + overlays
 * =========================
 */

function makeLegendFolderKml(meta, yearsbeginend, yearsCount) {
  const enableTimeSpan = Number(yearsCount) >= 2;

  let out = `<Folder><name>凡例</name>`;

  out += `
  <ScreenOverlay>
    <name>凡例本体</name>
    <visibility>1</visibility>
    <Icon><href>hanrei/${meta.no}_common.png</href></Icon>
    <overlayXY x="0" y="1" xunits="fraction" yunits="fraction"/>
    <screenXY x="0" y="1" xunits="fraction" yunits="fraction"/>
    <rotationXY x="0" y="0" xunits="fraction" yunits="fraction"/>
    <size x="0" y="0" xunits="fraction" yunits="fraction"/>
  </ScreenOverlay>
  `;

  yearsbeginend.forEach((pair) => {
    const begin = pair?.[0];
    const end = pair?.[1];

    if (begin == null || begin === "") return;

    const hasBeginEnd = begin != null && end != null && begin !== "" && end !== "";
    const timeSpan =
      enableTimeSpan && hasBeginEnd
        ? `<TimeSpan><begin>${begin}</begin><end>${end}</end></TimeSpan>`
        : "";

    out += `
    <ScreenOverlay>
      <name>${begin}年表示</name>
      <visibility>1</visibility>
      ${timeSpan}
      <Icon><href>hanrei/${meta.no}_${begin}_year.png</href></Icon>
      <overlayXY x="0" y="1" xunits="fraction" yunits="fraction"/>
      <screenXY x="0" y="1" xunits="fraction" yunits="fraction"/>
      <rotationXY x="0" y="0" xunits="fraction" yunits="fraction"/>
      <size x="0" y="0" xunits="fraction" yunits="fraction"/>
    </ScreenOverlay>
    `;
  });

  out += `</Folder>`;
  return out;
}

function kml_years_pre2(data999kml, yearsbeginend, yearsCount) {
  let kmlkml = "";

  const enableTimeSpan = Number(yearsCount) >= 2;

  yearsbeginend.forEach((pair, i) => {
    const kmlBody = data999kml?.[i] ?? "";
    if (!kmlBody) return;

    const begin = pair?.[0];
    const end = pair?.[1];

    if (begin == null || begin === "") return;

    const folderName = String(begin);

    const hasBeginEnd = begin != null && end != null && begin !== "" && end !== "";
    const timeSpan =
      enableTimeSpan && hasBeginEnd
        ? `<TimeSpan><begin>${begin}</begin><end>${end}</end></TimeSpan>`
        : "";

    kmlkml += `<Folder><name>${folderName}</name>${timeSpan}${kmlBody}</Folder>\n`;
  });

  return kmlkml;
}

function makekml_years(data999kml, yearbeginend, yearsCount) {
  return kml_years_pre2(data999kml, yearbeginend, yearsCount);
}

function toKmlAlpha(alpha) {
  const a = Number(alpha);

  if (!Number.isFinite(a)) return "c8";
  if (a <= 0.01) return "00";
  if (a <= 0.2) return "33";
  if (a <= 0.4) return "66";
  if (a <= 0.9) return "c8";
  return "ff";
}

function toKmlColorSafe(cssHex, alpha) {
  const s = String(cssHex ?? "").trim();

  // #RRGGBB 以外は受け付けない
  if (!/^#[0-9a-fA-F]{6}$/.test(s)) return null;

  const rr = s.slice(1, 3);
  const gg = s.slice(3, 5);
  const bb = s.slice(5, 7);
  const aa = toKmlAlpha(alpha);

  // KML color = AABBGGRR
  return `${aa}${bb}${gg}${rr}`;
}

/**
 * =========================
 * makekml
 * =========================
 */

function makekml(kml_body, viewarray, meta, area, alpha) {
  const kml1_colora = meta.kml1_color;
  const kml2_colora = meta.kml2_color;
  const kml3_colora = meta.kml3_color;
  const kml4_colora = meta.kml4_color;
  const kml5_colora = meta.kml5_color;

  console.log("[kml colors raw]", {
    kml1: meta.kml1_color,
    kml2: meta.kml2_color,
    kml3: meta.kml3_color,
    kml4: meta.kml4_color,
    kml5: meta.kml5_color,
    no: meta.no,
  });

  const kml1_color = toKmlColorSafe(kml1_colora, alpha);
  const kml2_color = toKmlColorSafe(kml2_colora, alpha);
  const kml3_color = toKmlColorSafe(kml3_colora, alpha);
  const kml4_color = toKmlColorSafe(kml4_colora, alpha);
  const kml5_color = toKmlColorSafe(kml5_colora, alpha);

  const title = meta.title;
  const cityname = area;
  const [latcenter, loncenter, lonheightratio] = viewarray;

  return Promise.resolve(
    `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${cityname + "_" + title}</name>

    <Style id="PolyCol1"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml1_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol2"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml2_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol3"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml3_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol4"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml4_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol5"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml5_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>

    <LookAt>
      <longitude>${loncenter}</longitude>
      <latitude>${latcenter}</latitude>
      <altitude>0</altitude>
      <range>${lonheightratio}</range>
      <tilt>30</tilt>
      <heading>-25</heading>
      <altitudeMode>absolute</altitudeMode>
    </LookAt>

    ${kml_body}

  </Document>
</kml>`
  );
}

/**
 * =========================
 * Center calculation
 * =========================
 */

function mesh2ndcenter(meshcode2nd) {
  const str = String(meshcode2nd);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));

  const lat = p / 1.5 + q / 12;
  const lon = u + 100 + v * 0.125;

  const lon2 = lon + 0.125;
  const lat2 = lat + 0.08333333;

  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  return [latc, lonc];
}

function mesh3rdcenter(meshcode3rd) {
  const str = String(meshcode3rd);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat = p / 1.5 + q / 12 + r / 120;
  const lon = u + 100 + v * 0.125 + w * 0.0125;

  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;

  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  return [latc, lonc];
}

function mesh3rdcenter_nihon(meshcode3rd) {
  const str = String(meshcode3rd);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat_p = p / 1.5 + q / 12 + r / 120;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;

  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;

  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  return [latc, lonc];
}

function mesh4thcenter(meshcode4th) {
  const str = String(meshcode4th);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;

  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  return [latc, lonc];
}

function mesh4thcenter_nihon(meshcode4th) {
  const str = String(meshcode4th);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat_p = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;

  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  return [latc, lonc];
}

function calcCenterFromRows(rows, meshLen, minValue, isNihonSokuchi = false) {
  if (!rows || rows.length === 0) return [0, 0, 100000];

  let pickCenterFn;
  if (meshLen === 6) {
    pickCenterFn = mesh2ndcenter;
  } else if (meshLen === 9) {
    pickCenterFn = isNihonSokuchi ? mesh4thcenter_nihon : mesh4thcenter;
  } else {
    pickCenterFn = isNihonSokuchi ? mesh3rdcenter_nihon : mesh3rdcenter;
  }

  let latmax = -Infinity;
  let latmin = Infinity;
  let lonmax = -Infinity;
  let lonmin = Infinity;

  for (let i = 0; i < rows.length; i++) {
    if (toNumber(rows[i][1]) > toNumber(minValue)) {
      const [lat, lon] = pickCenterFn(rows[i][0]);

      if (latmax < lat) latmax = lat;
      if (lonmax < lon) lonmax = lon;
      if (latmin > lat) latmin = lat;
      if (lonmin > lon) lonmin = lon;
    }
  }

  if (!isFinite(latmax) || !isFinite(latmin) || !isFinite(lonmax) || !isFinite(lonmin)) {
    return [0, 0, 100000];
  }

  const latcenter = (latmax + latmin) / 2;
  const loncenter = (lonmax + lonmin) / 2;

  let lonheightratio = 170000 * (lonmax - lonmin);
  if (lonmax <= lonmin) lonheightratio = 100000;

  return [latcenter, loncenter, lonheightratio];
}

/**
 * =========================
 * Component
 * =========================
 */

const Csv2kml_v2 = ({ meta, area, ratio, alpha, children }) => {
  const { color1set } = useContext(Color1Set);
  const { shikii1set } = useContext(Shikii1Set);

  const handlerClickDownloadButton = useCallback(
    async (e) => {
      e.preventDefault();

      const meta2 = buildMetaOverride(meta, color1set, shikii1set);

      console.log("[meta nihonsokuchi check]", {
        no: meta2.no,
        title: meta2.title,
        yearKeys: Object.keys(meta2).filter((k) => /^year\d+$/.test(k)),
        nihonKeys: Object.keys(meta2).filter((k) => /nihon|sokuchi/i.test(k)),
        year1: meta2.year1,
        year2: meta2.year2,
        year3: meta2.year3,
        year4: meta2.year4,
        year5: meta2.year5,
        year6: meta2.year6,
        year1_nihonsokuchi: meta2.year1_nihonsokuchi,
        year2_nihonsokuchi: meta2.year2_nihonsokuchi,
        year3_nihonsokuchi: meta2.year3_nihonsokuchi,
        year4_nihonsokuchi: meta2.year4_nihonsokuchi,
        year5_nihonsokuchi: meta2.year5_nihonsokuchi,
        year6_nihonsokuchi: meta2.year6_nihonsokuchi,
      });

      const [years, yearbeginend] = makeYears(meta2);
      const yearFlags = buildYearFlags(meta2);

      console.log("[yearFlags]", yearFlags);
      console.log(
        "[year and nihonsokuchi pair]",
        years.map((year, idx) => ({
          idx,
          year,
          nihon: yearFlags[idx]?.isNihonSokuchi ?? false,
        }))
      );

      const settled = await Promise.allSettled(
        years.map((year, idx) =>
          getkml2(
            meta2.no,
            year,
            area,
            meta2.csv_filename,
            meta2,
            ratio,
            yearFlags[idx]?.isNihonSokuchi ?? false
          )
        )
      );

      const yearResults = settled.map((s, idx) => {
        if (s.status === "fulfilled") return s.value;
        console.error("[getkml2 failed]", { year: years[idx], err: s.reason });
        return { kml: "", rows: [], meshLen: 0 };
      });

      const data999kml2 = yearResults.map((r) => r.kml);

      const idx5 = years.findIndex((y) => String(y) === String(meta2.year5));
      const centerIndex = idx5 >= 0 ? idx5 : (years.length - 1);
      const centerResult = yearResults[centerIndex] ?? { rows: [], meshLen: 0 };

      const centerMin =
        centerResult.meshLen === 6 ? toNumber(meta2.kokudo_min) : toNumber(meta2.min);

      const centerIsNihonSokuchi = yearFlags[centerIndex]?.isNihonSokuchi ?? false;

      console.log("[center nihonsokuchi]", { centerIndex, centerIsNihonSokuchi });

      const kmlcenter = calcCenterFromRows(
        centerResult.rows,
        centerResult.meshLen,
        centerMin,
        centerIsNihonSokuchi
      );

      const legendKml = makeLegendFolderKml(meta2, yearbeginend, years.length);
      const kmlYears = makekml_years(data999kml2, yearbeginend, years.length);
      const kmlll = await makekml(legendKml + kmlYears, kmlcenter, meta2, area, alpha);

      const zip = new JSZip();
      zip.file("doc.kml", kmlll);

      const onlyOneYear = years.filter((y) => y != null && y !== "").length === 1;

      const commonLegend = makeLegendCommonPng(meta2, ratio, onlyOneYear);
      const yearLegendPngs = makeLegendYearPngsForYears(meta2, years, onlyOneYear);

      const folder = zip.folder("hanrei");
      if (!folder) throw new Error("failed to create hanrei folder");

      folder.file(commonLegend.filename, commonLegend.base64, { base64: true });

      for (const p of yearLegendPngs) {
        folder.file(p.filename, p.base64, { base64: true });
      }

      const kmzBlob = await zip.generateAsync({ type: "blob" });
      saveAs(kmzBlob, `${area}_${meta2.csv_filename}.kmz`);
    },
    [meta, area, ratio, alpha, color1set, shikii1set]
  );

  return <button onClick={handlerClickDownloadButton}>{children}</button>;
};

export default Csv2kml_v2;